/**
 * Permissions Service for RBAC checks
 * Handles permission verification before showing pages
 */
// Use relative import so this service also works when dynamically imported from grc_frontend
import { getTprmApiBaseUrl } from '../utils/backendEnv'
import { tokenService } from './tokenService'

const API_BASE_URL = getTprmApiBaseUrl()

// Cache for permission checks to reduce API calls
const permissionCache = new Map()
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

// Debug: Log the API URL being used

class PermissionsService {
  /**
   * Check if user has a specific SLA permission
   * @param {string} permission - Permission to check (ViewSLA, CreateSLA, UpdateSLA, DeleteSLA, ActivateDeactivateSLA)
   * @returns {Promise<boolean>}
   */
  async hasSLAPermission(permission) {
    if (this.hasTprmElevatedAccess()) return true
    const user = this.getCurrentUser()

    // Support UserId, userid, id, user_id fields
    const userId = user?.UserId || user?.userid || user?.id || user?.user_id
    if (!user || !userId) {

      return false
    }

    const cacheKey = `sla_${userId}_${permission}`
    
    // Check cache first
    const cached = permissionCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {

      return cached.hasPermission
    }

    try {
      const token = tokenService.getToken()
      if (!token) {

        return false
      }

      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL
      const url = `${baseUrl}/rbac/sla/?permission_type=${permission}`

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()

        const hasPermission = data.has_permission || false

        // Cache the result
        permissionCache.set(cacheKey, {
          hasPermission,
          timestamp: Date.now()
        })

        return hasPermission
      } else {
        // If API fails, return false (deny access)
        const errorText = await response.text()

        return false
      }
    } catch (error) {

      // On error, deny access for security
      return false
    }
  }

  /**
   * Check if user has a specific Contract permission
   * @param {string} permission - Permission to check (e.g., CreateContract, PerformContractAudit)
   * @returns {Promise<boolean>}
   */
  async hasContractPermission(permission) {
    if (this.hasTprmElevatedAccess()) return true
    const user = this.getCurrentUser()

    const userId = user?.UserId || user?.userid || user?.id || user?.user_id
    if (!user || !userId) {

      return false
    }

    const cacheKey = `contract_${userId}_${permission}`

    const cached = permissionCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {

      return cached.hasPermission
    }

    try {
      const token = tokenService.getToken()
      if (!token) {

        return false
      }

      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL
      const url = `${baseUrl}/rbac/contract/?permission_type=${permission}`

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()

        const hasPermission = data.has_permission || false

        permissionCache.set(cacheKey, {
          hasPermission,
          timestamp: Date.now()
        })

        return hasPermission
      } else {
        const errorText = await response.text()

        return false
      }
    } catch (error) {

      return false
    }
  }

  /**
   * Check if user has a specific RFP permission
   * @param {string} permission - Permission to check (view_rfp, create_rfp, edit_rfp, delete_rfp, approve_rfp, evaluate_rfp, etc.)
   * @returns {Promise<boolean>}
   */
  async checkRFPPermission(permission) {
    if (this.hasTprmElevatedAccess()) return true
    const user = this.getCurrentUser()

    // Support UserId, userid, id, user_id fields
    const userId = user?.UserId || user?.userid || user?.id || user?.user_id
    if (!user || !userId) {

      return false
    }

    const cacheKey = `rfp_${userId}_${permission}`
    
    // Check cache first
    const cached = permissionCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {

      return cached.hasPermission
    }

    try {
      const token = tokenService.getToken()
      if (!token) {

        return false
      }

      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL
      const url = `${baseUrl}/rbac/rfp/?permission_type=${permission}`

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()

        const hasPermission = data.has_permission || false

        // Cache the result
        permissionCache.set(cacheKey, {
          hasPermission,
          timestamp: Date.now()
        })

        return hasPermission
      } else {
        // If API fails, return false (deny access)
        const errorText = await response.text()

        return false
      }
    } catch (error) {

      // On error, deny access for security
      return false
    }
  }

  /**
   * Check if user has a specific Vendor permission
   * @param {string} permission - Permission key (e.g. view_vendors, vendor_view)
   * @returns {Promise<boolean>}
   */
  async checkVendorPermission(permission) {
    if (this.hasTprmElevatedAccess()) return true
    const user = this.getCurrentUser()

    const userId = user?.UserId || user?.userid || user?.id || user?.user_id
    if (!user || !userId) {
      return false
    }

    const cacheKey = `vendor_${userId}_${permission}`

    const cached = permissionCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.hasPermission
    }

    try {
      const token = tokenService.getToken()
      if (!token) {
        return false
      }

      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL
      const url = `${baseUrl}/rbac/vendor/?permission_type=${encodeURIComponent(permission)}`

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        const hasPermission = data.has_permission || false
        permissionCache.set(cacheKey, {
          hasPermission,
          timestamp: Date.now(),
        })
        return hasPermission
      }
      await response.text()
      return false
    } catch (error) {
      return false
    }
  }

  /**
   * Check if user has a specific BCP/DRP permission
   * @param {string} permission - Permission to check (e.g., ViewPlansAndDocuments, CreateBCPDRPStrategyAndPlans)
   * @returns {Promise<boolean>}
   */
  async checkBCPPermission(permission) {
    if (this.hasTprmElevatedAccess()) return true
    const user = this.getCurrentUser()

    const userId = user?.UserId || user?.userid || user?.id || user?.user_id
    if (!user || !userId) {
      return false
    }

    const cacheKey = `bcp_${userId}_${permission}`

    const cached = permissionCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.hasPermission
    }

    try {
      const token = tokenService.getToken()
      if (!token) {
        return false
      }

      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL
      const url = `${baseUrl}/rbac/bcp-drp/?permission_type=${encodeURIComponent(permission)}`

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        const hasPermission = data.has_permission || false
        permissionCache.set(cacheKey, {
          hasPermission,
          timestamp: Date.now()
        })
        return hasPermission
      } else {
        await response.text()
        return false
      }
    } catch (error) {
      return false
    }
  }

  /**
   * Role-name bypass is intentionally disabled.
   * Access is determined purely by the permission flags in the rbac_tprm table,
   * regardless of the user's role label.
   */
  hasTprmElevatedAccess() {
    return false
  }

  /**
   * Check if user has a specific User Management permission
   * @param {string} permission - Permission to check (ViewUserManagement, CreateUserManagement, etc.)
   * @returns {Promise<boolean>}
   */
  async checkUserManagementPermission(permission) {
    if (this.hasTprmElevatedAccess()) return true
    const user = this.getCurrentUser()
    const userId = user?.UserId || user?.userid || user?.id || user?.user_id
    if (!user || !userId) return false

    const cacheKey = `user_mgmt_${userId}_${permission}`
    const cached = permissionCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.hasPermission
    }

    try {
      const token = tokenService.getToken()
      if (!token) return false
      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL
      const url = `${baseUrl}/rbac/user-management/?permission_type=${encodeURIComponent(permission)}`
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        const hasPermission = data.has_permission || false
        permissionCache.set(cacheKey, { hasPermission, timestamp: Date.now() })
        return hasPermission
      }
      return false
    } catch (error) {
      return false
    }
  }

  /**
   * Generic permission check that routes to the correct method based on permission type
   * @param {string} permission - Permission to check
   * @returns {Promise<boolean>}
   */
  async checkPermission(permission) {
    if (this.hasTprmElevatedAccess()) {
      return true
    }
    const loweredPermission = permission.toLowerCase()
    const isVendorPermission = loweredPermission.startsWith('vendor_')
    const isRFPPermission = loweredPermission.includes('rfp')
    const isContractPermission = loweredPermission.includes('contract')
    // Procurement modules (RFI, RFQ, Auction, Emergency, Direct) share the same rbac_tprm table as RFP
    const isProcurementPermission = loweredPermission.includes('rfi') || loweredPermission.includes('rfq') || loweredPermission.includes('auction') || loweredPermission.includes('emergency') || loweredPermission.includes('direct')
    // BCP/DRP permissions - explicit set matching every BCP column in rbac_tprm
    const bcpPermissions = new Set([
      'createbcpdrpstrategyandplans',       // CreateBCPDRPStrategyAndPlans
      'viewplansanddocuments',               // ViewPlansAndDocuments
      'assignplansforevaluation',            // AssignPlansForEvaluation
      'approveorrejectplanevaluations',      // ApproveOrRejectPlanEvaluations
      'ocrextractionandreview',              // OCRExtractionAndReview
      'createquestionnairefortesting',       // CreateQuestionnaireForTesting
      'reviewquestionnaireanswers',          // ReviewQuestionnaireAnswers
      'finalapprovalofplan',                 // FinalApprovalOfPlan
      'createquestionnaire',                 // CreateQuestionnaire
      'assignquestionnairesforreview',       // AssignQuestionnairesForReview
      'viewallquestionnaires',               // ViewAllQuestionnaires
      'configuresystemsettings',             // ConfigureSystemSettings
      'configurequestionnairesettings',      // ConfigureQuestionnaireSettings
      'createupdateuserroles',               // CreateUpdateUserRoles
      'managedocumentaccesscontrols',        // ManageDocumentAccessControls
      'generatecomplianceauditreports',      // GenerateComplianceAuditReports
      'viewdocumentstatushistory',           // ViewDocumentStatusHistory
      'requestdocumentrevisionsfromvendor',  // RequestDocumentRevisionsFromVendor
      'viewvendorsubmitteddocuments',        // ViewVendorSubmittedDocuments
      'viewbcpdrpplanstatus',                // ViewBCPDRPPlanStatus
      'coordinatevendorfeedback',            // CoordinateVendorFeedback
      'evaluateplanbasedoncriteria',         // EvaluatePlanBasedOnCriteria
      'submitevaluationfeedback',            // SubmitEvaluationFeedback
      'viewvendorcontracts',                 // ViewVendorContracts
      'createmodifycontracts',               // CreateModifyContracts
      'viewavailablevendors',                // ViewAvailableVendors
      'addvendortobcpdrpstrategy',           // AddVendorToBCPDRPStrategy
      'assessvendorrisk',                    // AssessVendorRisk
      'viewvendorriskscores',                // ViewVendorRiskScores
      'identifyrisksinplans',               // IdentifyRisksInPlans
      'viewidentifiedrisks',                 // ViewIdentifiedRisks
      'manageriskmitigationplans',           // ManageRiskMitigationPlans
      'viewriskmitigationstatus',            // ViewRiskMitigationStatus
      'viewcompliancestatusofplans',         // ViewComplianceStatusOfPlans
      'auditcomplianceofdocuments',          // AuditComplianceOfDocuments
      'configuredocumentsecuritysettings',   // ConfigureDocumentSecuritySettings
      'viewdocumentaccesslogs',              // ViewDocumentAccessLogs
      'reviewregulatorycompliance',          // ReviewRegulatoryCompliance
      'auditcomplianceagainstregulations',   // AuditComplianceAgainstRegulations
      'reviewandapprovelegalaspectsofplans', // ReviewAndApproveLegalAspectsOfPlans
      'generatelegalcompliancereports',      // GenerateLegalComplianceReports
      'viewcontractualobligations',          // ViewContractualObligations
      'auditplandocumentation',              // AuditPlanDocumentation
      'viewauditlogs',                       // ViewAuditLogs
      'generateinternalauditreports',        // GenerateInternalAuditReports
      'conductexternalcomplianceaudit',      // ConductExternalComplianceAudit
      'generateexternalauditreports',        // GenerateExternalAuditReports
      'reviewexternalauditorcomments',       // ReviewExternalAuditorComments
      'auditcomplianceofplans',              // AuditComplianceOfPlans
      'viewcomplianceauditresults',          // ViewComplianceAuditResults
      'generatecompliancereports',           // GenerateComplianceReports
      'manageserverresourcesforbcpdrp',      // ManageServerResourcesForBCPDRP
      'monitorsystemhealth',                 // MonitorSystemHealth
      'backupsystemconfiguration',           // BackupSystemConfiguration
      'viewincidentresponseplans',           // ViewIncidentResponsePlans
      'createincidentresponseplans',         // CreateIncidentResponsePlans
      'updateincidentresponseplans',         // UpdateIncidentResponsePlans
      'integratebcpdrpwithexternalsystems',  // IntegrateBCPDRPWithExternalSystems
      'manageintegrationsettings',           // ManageIntegrationSettings
    ])
    const isBCPPermission = bcpPermissions.has(loweredPermission)
    // User Management permissions
    const isUserManagementPermission = loweredPermission.includes('usermanagement') || loweredPermission.includes('user_management') || loweredPermission === 'manageuserpermissions'

    if (isVendorPermission) {
      return this.checkVendorPermission(permission)
    }
    if (isRFPPermission || isProcurementPermission) {
      return this.checkRFPPermission(permission)
    }
    if (isContractPermission) {
      return this.hasContractPermission(permission)
    }
    if (isBCPPermission) {
      return this.checkBCPPermission(permission)
    }
    if (isUserManagementPermission) {
      return this.checkUserManagementPermission(permission)
    }
    return this.hasSLAPermission(permission)
  }

  getCurrentUser() {
    try {
      let userStr = localStorage.getItem('current_user')
      if (!userStr) userStr = localStorage.getItem('user')
      return userStr ? JSON.parse(userStr) : null
    } catch (error) {
      return null
    }
  }

  /**
   * Clear cached RBAC permission checks (call after login, logout, or tenant/user change).
   */
  clearCache() {
    permissionCache.clear()
  }
}

const permissionsService = new PermissionsService()
export default permissionsService

